import { Close } from "@mui/icons-material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { getStripeForAccount, hostname, parseError } from "../../../utils";
import {
  SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION,
  SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY,
} from "../../../constants";

export interface AddOnPackage {
  id: string;
  name: string;
  description?: string;
  shortDescription?: string;
  packageType: string;
  price: number;
  currency: string;
  isActive: boolean;
  isFeatured: boolean;
  iconName?: string;
  featuresIncluded?: string[];
  billingType?: string;
  billingInterval?: string;
}

interface AddOnsDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  eventListener: Function;
  onSuccess?: () => void;
  isPaidUser?: boolean;
  mode?: string;
  onOpenSubscription?: () => void;
}

// Payment form component that uses Stripe Elements
const AddOnsPaymentForm = ({
  selectedPackages,
  totalAmount,
  onSuccess,
  onCancel,
  getToken,
  eventListener,
  connectedAccount,
}: {
  selectedPackages: AddOnPackage[];
  totalAmount: number;
  onSuccess: () => void;
  onCancel: () => void;
  getToken: () => Promise<string>;
  eventListener: Function;
  connectedAccount?: string;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getBillingLabel = (pkg: AddOnPackage) => {
    if (pkg.billingType === SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION) {
      return pkg.billingInterval === SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY
        ? "/month"
        : "/year";
    }
    return "one-time";
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      // Submit the payment element to collect the payment method
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      // Confirm the setup intent to get the payment method
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      if (setupIntent && setupIntent.status === "succeeded") {
        // Now charge the packages with the payment method
        const token = await getToken();
        const response = await axios.post(
          `${hostname}/addons/confirm`,
          {
            packageIds: selectedPackages.map((pkg) => pkg.id),
            paymentMethodId: setupIntent.payment_method,
            connectedAccount: connectedAccount,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        eventListener({
          type: "SET_SUCCESS",
          message: response.data.message || "Add-ons purchased successfully!",
        });

        onSuccess();
      }
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setProcessing(false);
    }
  };

  // Calculate totals by billing type
  const oneTimeTotal = selectedPackages
    .filter((pkg) => pkg.billingType !== SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION)
    .reduce((sum, pkg) => sum + pkg.price, 0);

  const monthlyTotal = selectedPackages
    .filter(
      (pkg) =>
        pkg.billingType === SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION &&
        pkg.billingInterval === SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY
    )
    .reduce((sum, pkg) => sum + pkg.price, 0);

  const annualTotal = selectedPackages
    .filter(
      (pkg) =>
        pkg.billingType === SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION &&
        pkg.billingInterval !== SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY
    )
    .reduce((sum, pkg) => sum + pkg.price, 0);

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Order Summary
        </Typography>
        {selectedPackages.map((pkg) => (
          <Box
            key={pkg.id}
            sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
          >
            <Typography>{pkg.name}</Typography>
            <Typography fontWeight="bold">
              {formatPrice(pkg.price)} {getBillingLabel(pkg)}
            </Typography>
          </Box>
        ))}
        <Box sx={{ borderTop: 1, borderColor: "divider", pt: 1, mt: 2 }}>
          {oneTimeTotal > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography>One-time charges</Typography>
              <Typography fontWeight="bold">{formatPrice(oneTimeTotal)}</Typography>
            </Box>
          )}
          {monthlyTotal > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography>Monthly charges</Typography>
              <Typography fontWeight="bold">{formatPrice(monthlyTotal)}/month</Typography>
            </Box>
          )}
          {annualTotal > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography>Annual charges</Typography>
              <Typography fontWeight="bold">{formatPrice(annualTotal)}/year</Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Typography fontWeight="bold">Due Today</Typography>
            <Typography fontWeight="bold">{formatPrice(totalAmount)}</Typography>
          </Box>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Payment Method
        </Typography>
        <PaymentElement
          options={{
            layout: {
              type: "tabs",
              defaultCollapsed: false,
            },
            wallets: {
              applePay: "auto",
              googlePay: "auto",
            },
          }}
        />
      </Paper>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Button onClick={onCancel} disabled={processing}>
          Back
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          loading={processing}
          disabled={!stripe || !elements}
        >
          Confirm {formatPrice(totalAmount)}
        </LoadingButton>
      </Box>
    </Box>
  );
};

const AddOnsDialog: React.FC<AddOnsDialogProps> = ({
  open,
  onClose,
  getToken,
  eventListener,
  onSuccess,
  isPaidUser = false,
  mode = "light",
  onOpenSubscription,
}) => {
  const [packages, setPackages] = useState<AddOnPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([]);
  const [step, setStep] = useState<"select" | "payment">("select");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [connectedAccount, setConnectedAccount] = useState<string | undefined>(
    undefined
  );
  const [creatingPayment, setCreatingPayment] = useState(false);

  // Get the appropriate Stripe instance (platform or connected account for direct charges)
  const stripeInstance = useMemo(() => {
    return getStripeForAccount(connectedAccount);
  }, [connectedAccount]);

  useEffect(() => {
    if (open) {
      loadPackages();
      setStep("select");
      setSelectedPackageIds([]);
      setClientSecret(null);
      setConnectedAccount(undefined);
    }
  }, [open]);

  const loadPackages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${hostname}/service-packages`, {
        params: { domain: window.location.hostname },
      });

      if (response.data.success) {
        setPackages(response.data.packages || []);
      }
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePackageToggle = (packageId: string) => {
    if (selectedPackageIds.includes(packageId)) {
      setSelectedPackageIds(selectedPackageIds.filter((id) => id !== packageId));
    } else {
      setSelectedPackageIds([...selectedPackageIds, packageId]);
    }
  };

  const selectedPackages = packages.filter((pkg) =>
    selectedPackageIds.includes(pkg.id)
  );

  const totalAmount = selectedPackages.reduce((sum, pkg) => sum + pkg.price, 0);

  const handleContinueToPayment = async () => {
    if (selectedPackageIds.length === 0) return;

    setCreatingPayment(true);

    try {
      const token = await getToken();

      const response = await axios.post(
        `${hostname}/addons/create-payment`,
        {
          packageIds: selectedPackageIds,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setClientSecret(response.data.clientSecret);
      setConnectedAccount(response.data.connectedAccount);
      setStep("payment");
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setCreatingPayment(false);
    }
  };

  const handlePaymentSuccess = () => {
    onClose();
    if (onSuccess) {
      onSuccess();
    }
  };

  const handleBackToSelect = () => {
    setStep("select");
    setClientSecret(null);
    setConnectedAccount(undefined);
  };

  const handleUpgradeClick = () => {
    onClose();
    if (onOpenSubscription) {
      onOpenSubscription();
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getBillingLabel = (pkg: AddOnPackage) => {
    if (pkg.billingType === SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION) {
      return pkg.billingInterval === SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY
        ? "/month"
        : "/year";
    }
    return "one-time";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "500px",
          maxHeight: "90vh",
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ display: "flex", height: "100%", minHeight: 0 }}>
        {/* Left side - Image */}
        <Box
          sx={{
            width: "40%",
            minHeight: "500px",
            background:
              "linear-gradient(135deg, #0d1b2a 0%, #1b263b 50%, #415a77 100%)",
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative elements */}
          <Box
            sx={{
              position: "absolute",
              width: "200px",
              height: "200px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(100,200,255,0.3) 0%, transparent 70%)",
              top: "20%",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: "150px",
              height: "150px",
              border: "2px solid rgba(255,255,255,0.1)",
              borderRadius: "20px",
              top: "25%",
              left: "50%",
              transform: "translateX(-50%) rotate(45deg)",
            }}
          />
          {/* Stars */}
          {[...Array(20)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                width: Math.random() * 3 + 1 + "px",
                height: Math.random() * 3 + 1 + "px",
                backgroundColor: "rgba(255,255,255,0.5)",
                borderRadius: "50%",
                top: Math.random() * 100 + "%",
                left: Math.random() * 100 + "%",
              }}
            />
          ))}
          <Box sx={{ zIndex: 1, textAlign: "center", px: 3 }}>
            <Typography
              variant="h4"
              sx={{
                color: "white",
                fontWeight: "bold",
                textShadow: "0 0 20px rgba(100,200,255,0.5)",
                mb: 2,
              }}
            >
              Power Up
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255,255,255,0.8)",
                lineHeight: 1.6,
              }}
            >
              Expand your capabilities with powerful add-ons.
            </Typography>
          </Box>
        </Box>

        {/* Right side - Content */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <DialogContent
            sx={{
              flex: 1,
              p: 3,
              overflow: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 3,
              }}
            >
              <Typography variant="h5" fontWeight="bold">
                Add-ons
              </Typography>
              <IconButton onClick={onClose} size="small">
                <Close />
              </IconButton>
            </Box>

            {step === "select" && (
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Enhance your subscription with additional features and
                  capabilities.
                </Typography>

                {!isPaidUser && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    A subscription is required to purchase add-ons.{" "}
                    <Button
                      size="small"
                      onClick={handleUpgradeClick}
                      sx={{ ml: 1 }}
                    >
                      Subscribe Now
                    </Button>
                  </Alert>
                )}

                {loading ? (
                  <Typography>Loading add-ons...</Typography>
                ) : packages.length === 0 ? (
                  <Typography color="text.secondary">
                    No add-ons available at this time.
                  </Typography>
                ) : (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {packages.map((pkg) => {
                      const isSelected = selectedPackageIds.includes(pkg.id);

                      return (
                        <Paper
                          key={pkg.id}
                          variant="outlined"
                          onClick={() => isPaidUser && handlePackageToggle(pkg.id)}
                          sx={{
                            p: 2,
                            cursor: isPaidUser ? "pointer" : "not-allowed",
                            opacity: isPaidUser ? 1 : 0.7,
                            position: "relative",
                            border: isSelected ? 2 : 1,
                            borderColor: isSelected
                              ? "primary.main"
                              : "divider",
                            "&:hover": isPaidUser
                              ? {
                                  borderColor: "primary.main",
                                  bgcolor: "action.hover",
                                }
                              : {},
                          }}
                        >
                          {pkg.isFeatured && (
                            <Chip
                              label="Popular"
                              size="small"
                              color="primary"
                              sx={{
                                position: "absolute",
                                top: -10,
                                right: 16,
                              }}
                            />
                          )}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 2,
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  disabled={!isPaidUser}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() => handlePackageToggle(pkg.id)}
                                  color="primary"
                                />
                              }
                              label=""
                              sx={{ m: 0 }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Box>
                                  <Typography variant="body1" fontWeight="500">
                                    {pkg.name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {pkg.shortDescription || pkg.description}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: "right" }}>
                                  <Typography variant="h6" fontWeight="bold">
                                    {formatPrice(pkg.price)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {getBillingLabel(pkg)}
                                  </Typography>
                                </Box>
                              </Box>

                              {pkg.featuresIncluded &&
                                pkg.featuresIncluded.length > 0 && (
                                  <Box sx={{ mt: 2 }}>
                                    <Divider sx={{ mb: 1 }} />
                                    {pkg.featuresIncluded.map(
                                      (feature, index) => (
                                        <Box
                                          key={index}
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 0.5,
                                          }}
                                        >
                                          <CheckCircleOutlineIcon
                                            sx={{
                                              fontSize: 16,
                                              color: "primary.main",
                                            }}
                                          />
                                          <Typography variant="body2">
                                            {feature}
                                          </Typography>
                                        </Box>
                                      )
                                    )}
                                  </Box>
                                )}
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                )}

                {/* Summary */}
                {selectedPackageIds.length > 0 && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      bgcolor: "action.hover",
                    }}
                  >
                    <Typography variant="body1">
                      {selectedPackageIds.length} add-on
                      {selectedPackageIds.length > 1 ? "s" : ""} selected
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                      {formatPrice(totalAmount)} total
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <LoadingButton
                    variant="contained"
                    size="large"
                    onClick={handleContinueToPayment}
                    loading={creatingPayment}
                    disabled={selectedPackageIds.length === 0 || !isPaidUser}
                    sx={{ minWidth: "200px" }}
                  >
                    Continue to Payment
                  </LoadingButton>
                </Box>
              </Box>
            )}

            {step === "payment" && clientSecret && selectedPackages.length > 0 && (
              <Elements
                stripe={stripeInstance}
                options={{
                  clientSecret,
                  appearance: {
                    theme: mode === "dark" ? "night" : "stripe",
                  },
                }}
              >
                <AddOnsPaymentForm
                  selectedPackages={selectedPackages}
                  totalAmount={totalAmount}
                  onSuccess={handlePaymentSuccess}
                  onCancel={handleBackToSelect}
                  getToken={getToken}
                  eventListener={eventListener}
                  connectedAccount={connectedAccount}
                />
              </Elements>
            )}
          </DialogContent>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AddOnsDialog;
